import React, { useRef, useState } from "react";
import { useToast } from "@finitless/shared/hooks";

const ImageUploader = ({
  onImageChange,
  className = "",
  maxSize = 2097152, // 2MB
  accept = "image/png, image/jpeg, image/jpg",
  children,
  disabled = false,
}) => {
  const fileInputRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const toast = useToast();

  const handleFile = (file) => {
    if (!file) return;

    if (file.size > maxSize) {
      toast.error("File too large", {
        description: `Image must be less than ${Math.round(
          maxSize / 1024 / 1024,
        )}MB`,
      });
      return;
    }

    // Basic type check
    if (
      accept !== "*" &&
      !accept.includes(file.type) &&
      !file.type.startsWith("image/")
    ) {
      toast.error("Invalid file type", {
        description: "Please upload a valid image file",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      if (onImageChange) {
        onImageChange(file, e.target.result);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
  };

  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleInputChange = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFile(files[0]);
    }
    // Reset input value to allow selecting the same file again
    e.target.value = "";
  };

  return (
    <div
      onClick={handleClick}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`${className} ${
        isDragging
          ? "border-blue-500 bg-gray-900/40 ring-2 ring-blue-500/20"
          : ""
      }`}
    >
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleInputChange}
        accept={accept}
        className="hidden"
        disabled={disabled}
      />
      {children}
    </div>
  );
};

export default ImageUploader;
